import { objectId } from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { SchemaType } from "./advanced-flow/schemas/schemas";

export const GenerationMode = { Agent: "agent", Template: "template" } as const;
export type GenerationMode =
  (typeof GenerationMode)[keyof typeof GenerationMode];

export const ClassifierAction = {
  ScenarioOutreach: "scenario-outreach",
  ScenarioReply: "scenario-reply",
  Fallback: "fallback",
  NoReply: "no-reply",
} as const;
export type ClassifierAction =
  (typeof ClassifierAction)[keyof typeof ClassifierAction];

export const ScenarioType = {
  DiscountOffer: "discount-offer",
  SubscriptionUpsell: "subscription-upsell",
  Custom: "custom",
} as const;
export type ScenarioType = (typeof ScenarioType)[keyof typeof ScenarioType];

const scenarioBaseSchema_ = z.object({
  _id: objectId().optional(),
  enabled: z.boolean(),
  mode: createEnum(GenerationMode, { enumName: "GenerationMode" }),
  outreachTemplate: z.string(),
  replyTemplate: z.string().nullish(),
});

const builtInScenarioSchema_ = scenarioBaseSchema_.extend({
  type: createEnum(
    Object.values(ScenarioType).filter(
      (v): v is Exclude<ScenarioType, "custom"> => v !== ScenarioType.Custom,
    ),
    { enumName: "ScenarioType", isPartial: true, originalEnum: ScenarioType },
  ),
});
interface BuiltInScenario extends z.infer<typeof builtInScenarioSchema_> {}

const customScenarioSchema_ = scenarioBaseSchema_.extend({
  type: z.literal(ScenarioType.Custom),
  name: z.string(),
});
interface CustomScenario extends z.infer<typeof customScenarioSchema_> {}

export const scenarioSchema = z.union([
  builtInScenarioSchema_,
  customScenarioSchema_,
]);
export type Scenario = BuiltInScenario | CustomScenario;

export const fallbackSchema = z.object({
  mode: createEnum(GenerationMode, { enumName: "GenerationMode" }),
  template: z.string(),
  customInstructions: z.string().optional(),
});
const fallbackSchema_ = fallbackSchema;

// ===================================================
//   Main Schema
// ===================================================

const recoverConfigSchema_ = z.object({
  _id: objectId(),
  teamId: objectId(),
  name: z.string(),
  discountId: objectId().nullish(),
  agentName: z.string().optional(),
  brandName: z.string().optional(),
  supportEmail: z.email().optional(),
  /** Array of scenarios from which the classifier selects */
  scenarios: z.array(scenarioSchema),
  /** In the case no scenarios conform to the current conversation, use this as a fallback */
  fallback: fallbackSchema_,
  /**
   * Schema of the trigger that drives this recover step. Determines which
   * dynamic variables are available in the message editor. Captured at config
   * creation time from the flow editor; legacy configs with no value default
   * to MARKETING_CHECKOUT_ABANDONMENT at read time.
   */
  schemaType: createEnum(SchemaType, { enumName: "SchemaType" }).optional(),
  /** Temporary field to track which SMS template this config was migrated from */
  migratedFromSmsTemplateId: objectId().optional(),
});
export const recoverConfigSchema: z.ZodType<RecoverConfig> =
  recoverConfigSchema_;
export interface RecoverConfig extends z.infer<typeof recoverConfigSchema_> {}

// ===================================================
//   Utils
// ===================================================

export type BuiltInScenarioType = Exclude<ScenarioType, "custom">;

const SCENARIO_LABELS: Record<BuiltInScenarioType, string> = {
  [ScenarioType.DiscountOffer]: "Discount offer",
  [ScenarioType.SubscriptionUpsell]: "Subscription upsell",
};

const MODE_LABELS: Record<GenerationMode, string> = {
  [GenerationMode.Agent]: "Agent",
  [GenerationMode.Template]: "Template",
};

export function getScenarioLabel(scenario: Scenario): string {
  if (isCustomScenario(scenario)) {
    return scenario.name || "Custom";
  }
  return SCENARIO_LABELS[scenario.type] ?? scenario.type;
}

export function getModeLabel(scenario: Scenario): string {
  return MODE_LABELS[scenario.mode] ?? scenario.mode;
}

export function isBuiltInScenario(
  scenario: Scenario,
): scenario is BuiltInScenario {
  return scenario.type !== ScenarioType.Custom;
}

export function isCustomScenario(
  scenario: Scenario,
): scenario is CustomScenario {
  return scenario.type === ScenarioType.Custom;
}

export function getBuiltInScenario(
  scenarios: Scenario[],
  type: BuiltInScenarioType,
): BuiltInScenario | undefined {
  return scenarios.find((s): s is BuiltInScenario => s.type === type);
}

export function getCustomScenarios(scenarios: Scenario[]): CustomScenario[] {
  return scenarios.filter(isCustomScenario);
}

export function getEnabledScenarios(scenarios: Scenario[]): Scenario[] {
  return scenarios.filter((s) => s.enabled);
}

export function getScenarioById(
  scenarios: Scenario[],
  scenarioId: string,
): Scenario | undefined {
  return scenarios.find((s) => s._id?.toString() === scenarioId);
}
